import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface PmAdditionalActState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  companyId: string;
  pmOfferId: any;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  enumPmOfferContractType: any;
  enumPmOfferContractTypeId: any;
  enumPmContractStatus: any;
  enumPmContractStatusId: any;
  metadata: any;
  remarks: any;
  haveFile: any;
  file: any;
  registrationNumber: any;
  accountManager: any;
  accountSale: any;
  company: any;
  pmContractIntervals: any[];
  pmContractWorkPlaceItems: any[];
  pmContractLaborExtras: any[];
  pmContractCommentsTab: any[];
  location: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: PmAdditionalActState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  companyId: '',
  pmOfferId: '',
  enumPmOfferContractTypeId: 0,
  enumPmContractStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  remarks: '',
  registrationNumber: '',
  haveFile: false,
  file: '',
  metadata: null,
  accountManager: null,
  accountSale: null,
  company: null,
  enumPmOfferContractType: null,
  enumPmContractStatus: null,
  pmContractIntervals: [],
  pmContractWorkPlaceItems: [],
  pmContractLaborExtras: [],
  pmContractCommentsTab: [],
  location: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
};

const pmAdditionalActSlice = createSlice({
  name: 'pmAdditionalAct',
  initialState,
  reducers: {
    setPmAdditional: (state, action: PayloadAction<Partial<PmAdditionalActState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addPmContractInterval: (state, action: PayloadAction<{ date: string; price: number }>) => {
      state.pmContractIntervals.push(action.payload);
    },
    updatePmContractInterval: (state, action: PayloadAction<{ index: number; updates: Partial<any> }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractIntervals[index]) {
        state.pmContractIntervals[index] = {
          ...state.pmContractIntervals[index],
          ...updates,
        };
      }
    },
    removePmContractInterval: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractIntervals.length) {
        state.pmContractIntervals.splice(index, 1);
      }
    },
    addPmAdditionalWorkPlaceItem: (state, action) => {
      state.pmContractWorkPlaceItems.push(action.payload);
    },
    updatePmAdditionalWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractWorkPlaceItems[index]) {
        state.pmContractWorkPlaceItems[index] = {
          ...state.pmContractWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removePmAdditionalWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractWorkPlaceItems.length) {
        state.pmContractWorkPlaceItems.splice(index, 1);
      }
    },
    addPmAdditionalLaborExtra: (state, action) => {
      state.pmContractLaborExtras.push(action.payload);
    },
    addPmAdditionalComment: (state, action: PayloadAction<any>) => {
      state.pmContractCommentsTab.push(action.payload);
    },
    updatePmAdditionalComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.pmContractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        // Update existing comment
        state.pmContractCommentsTab[index] = {
          ...state.pmContractCommentsTab[index],
          ...updates,
        };
      }
    },
    // Remove a file from a comment
    removePmAdditionalComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.pmContractCommentsTab.findIndex(
        (comment) => comment.departmentName === departmentName
      );

      if (commentIndex !== -1) {
        const comment = state.pmContractCommentsTab[commentIndex];

        // Filter out the file
        comment.pmContractCommentFiles = comment.pmContractCommentFiles.filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        // Update the comment in the array
        state.pmContractCommentsTab[commentIndex] = {
          ...comment,
          pmContractCommentFiles: comment.pmContractCommentFiles,
        };
      }
    },
  },
});

export const {
  setPmAdditional,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addPmContractInterval,
  updatePmContractInterval,
  removePmContractInterval,
  addPmAdditionalWorkPlaceItem,
  updatePmAdditionalWorkPlaceItem,
  removePmAdditionalWorkPlaceItem,
  addPmAdditionalLaborExtra,
  addPmAdditionalComment,
  updatePmAdditionalComment,
  removePmAdditionalComment,
} = pmAdditionalActSlice.actions;

export default pmAdditionalActSlice.reducer;
